/* eslint-disable */
import React, { useState } from "react";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    TextField
} from "@mui/material";
// @ts-ignore
import { sourceFormConfig } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/source-form-info.js";

import { SourceConfiguratorTool } from "src/modules/striim-ai-sidebar/tools/tql-tools/source-configurator-tool";
import { SourceMode } from "src/modules/striim-ai-sidebar/tools/tql-tools/source-configurator/source-configurator.types";
import BaseSimpleSourceAI from "src/modules/striim-ai-sidebar/tools/tql-tools/source-configurator/pages/source-form-config/base-simple-source/base-simple-source-ai";
import BaseSimpleTargetAI from "src/modules/striim-ai-sidebar/tools/tql-tools/target-configurator/target-form-config/base-simple-target/base-simple-target-ai";
// @ts-ignore
import componentService from "core/services/componentService/component-service";
import TargetFormConfigAI from "src/modules/striim-ai-sidebar/tools/tql-tools/target-configurator/target-form-config/target-form-config-ai";
import { TargetConfiguratorTool } from "src/modules/striim-ai-sidebar/tools/tql-tools/target-configurator-tool";

const MODES: SourceMode[] = ["Initial Load", "Change Data Capture", "Automated"];

const SOURCE_TYPES = Object.keys(sourceFormConfig).sort();

// Selectable source adapter component IDs (e.g. "OracleReader") for BaseSimpleSourceAI.
const SOURCE_ADAPTERS: string[] = componentService
    .getIDs()
    .filter(id => componentService.getType(id) === "SOURCE" && !!componentService.getAdapter(id))
    .sort();

const styles = {
    page: { p: 3, maxWidth: 720, mx: "auto" },
    controls: { display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" },
    result: {
        mt: 3,
        p: 2,
        backgroundColor: "#1e1e1e",
        color: "#d4d4d4",
        borderRadius: 1,
        fontFamily: "monospace",
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-all"
    }
};

/**
 * Hidden debug page (not linked anywhere) at #/debug/tools.
 * Mounts the AI sidebar's SourceConfiguratorTool standalone so it can be
 * exercised and debugged against the live API outside the chat sidebar.
 */
const DebugTools: React.FC = () => {
    const [sourceType, setSourceType] = useState<string>("POSTGRES");
    const [mode, setMode] = useState<SourceMode>("Initial Load");
    const [instanceKey, setInstanceKey] = useState(0);
    const [result, setResult] = useState<string | null>(null);

    const [sourceAdapterID, setSourceAdapterID] = useState<string>(
        SOURCE_ADAPTERS.includes("OracleReader") ? "OracleReader" : SOURCE_ADAPTERS[0] ?? ""
    );
    const [sourceAiAppName, setSourceAiAppName] = useState<string>("Admin.MyApp");
    const [sourceAiInstanceKey, setSourceAiInstanceKey] = useState(0);
    const [sourceAiResult, setSourceAiResult] = useState<string | null>(null);

    const [targetComponentID, setTargetComponentID] = useState<string>("GooglePubSubWriter");
    const [targetAppName, setTargetAppName] = useState<string>("Admin.MyApp");
    const [targetInstanceKey, setTargetInstanceKey] = useState(0);
    const [targetResult, setTargetResult] = useState<string | null>(null);

    const [targetFormType, setTargetFormType] = useState<string>("SnowflakeWriter");
    const [targetFormSource, setTargetFormSource] = useState<string>("");
    const [targetFormInstanceKey, setTargetFormInstanceKey] = useState(0);
    const [targetFormResult, setTargetFormResult] = useState<string | null>(null);

    const [targetConfigType, setTargetConfigType] = useState<string>("SnowflakeWriter");
    const [targetConfigInstanceKey, setTargetConfigInstanceKey] = useState(0);
    const [targetConfigResult, setTargetConfigResult] = useState<string | null>(null);

    const remount = () => {
        setResult(null);
        setInstanceKey(k => k + 1);
    };

    const remountTarget = () => {
        setTargetResult(null);
        setTargetInstanceKey(k => k + 1);
    };

    const handleRespond = (r: string) => {
        // Surface what the tool reports back to the chat runtime.
        // eslint-disable-next-line no-console
        console.log("[debug-tools] SourceConfiguratorTool respond:", r);
        setResult(r);
    };

    const remountSourceAi = () => {
        setSourceAiResult(null);
        setSourceAiInstanceKey(k => k + 1);
    };

    const handleSourceAiRespond = (data: Record<string, any>) => {
        const r = JSON.stringify(data, null, 2);
        // eslint-disable-next-line no-console
        console.log("[debug-tools] BaseSimpleSourceAI save:", r);
        setSourceAiResult(r);
    };

    const handleTargetRespond = (data: Record<string, any>) => {
        const r = JSON.stringify(data, null, 2);
        // eslint-disable-next-line no-console
        console.log("[debug-tools] BaseSimpleTargetAI save:", r);
        setTargetResult(r);
    };

    return (
        <Box sx={styles.page}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
                Debug · Tools
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Standalone harness for AI sidebar tools. Not linked anywhere.
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                Source Configurator
            </Typography>

            <Box sx={styles.controls}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="debug-source-type-label">Source Type</InputLabel>
                    <Select
                        labelId="debug-source-type-label"
                        label="Source Type"
                        value={sourceType}
                        onChange={e => {
                            setSourceType(e.target.value);
                            remount();
                        }}
                    >
                        {SOURCE_TYPES.map(t => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <RadioGroup
                    row
                    value={mode}
                    onChange={e => {
                        setMode(e.target.value as SourceMode);
                        remount();
                    }}
                >
                    {MODES.map(m => (
                        <FormControlLabel key={m} value={m} control={<Radio size="small" />} label={m} />
                    ))}
                </RadioGroup>
            </Box>

            <SourceConfiguratorTool
                key={`${sourceType}-${mode}-${instanceKey}`}
                args={{ sourceType, mode }}
                respond={handleRespond}
            />

            {result !== null && (
                <Box sx={styles.result}>
                    {(() => {
                        try {
                            return JSON.stringify(JSON.parse(result), null, 2);
                        } catch {
                            return result;
                        }
                    })()}
                </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                Base Simple Source (AI)
            </Typography>

            <Box sx={styles.controls}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="debug-source-adapter-label">Adapter</InputLabel>
                    <Select
                        labelId="debug-source-adapter-label"
                        label="Adapter"
                        value={sourceAdapterID}
                        onChange={e => {
                            setSourceAdapterID(e.target.value);
                            remountSourceAi();
                        }}
                    >
                        {SOURCE_ADAPTERS.map(id => (
                            <MenuItem key={id} value={id}>
                                {id}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    label="App Name"
                    value={sourceAiAppName}
                    onChange={e => {
                        setSourceAiAppName(e.target.value);
                        remountSourceAi();
                    }}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            <BaseSimpleSourceAI
                key={`${sourceAdapterID}-${sourceAiAppName}-${sourceAiInstanceKey}`}
                componentID={sourceAdapterID as any}
                appName={sourceAiAppName}
                onSave={handleSourceAiRespond}
            />

            {sourceAiResult !== null && <Box sx={styles.result}>{sourceAiResult}</Box>}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                Target Configurator
            </Typography>

            <Box sx={styles.controls}>
                <TextField
                    size="small"
                    label="Component ID"
                    value={targetComponentID}
                    onChange={e => {
                        setTargetComponentID(e.target.value);
                        remountTarget();
                    }}
                    sx={{ minWidth: 220 }}
                />
                <TextField
                    size="small"
                    label="App Name"
                    value={targetAppName}
                    onChange={e => {
                        setTargetAppName(e.target.value);
                        remountTarget();
                    }}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            <BaseSimpleTargetAI
                key={`${targetComponentID}-${targetAppName}-${targetInstanceKey}`}
                componentID={targetComponentID}
                appName={targetAppName}
                onSave={handleTargetRespond}
            />

            {targetResult !== null && <Box sx={styles.result}>{targetResult}</Box>}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                Target Form Config (StriimFormBuilder)
            </Typography>

            <Box sx={styles.controls}>
                <TextField
                    size="small"
                    label="Target Type"
                    value={targetFormType}
                    onChange={e => {
                        setTargetFormType(e.target.value);
                        setTargetFormInstanceKey(k => k + 1);
                        setTargetFormResult(null);
                    }}
                    sx={{ minWidth: 220 }}
                />
                <TextField
                    size="small"
                    label="Source (optional)"
                    value={targetFormSource}
                    onChange={e => {
                        setTargetFormSource(e.target.value);
                        setTargetFormInstanceKey(k => k + 1);
                        setTargetFormResult(null);
                    }}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            <TargetFormConfigAI
                key={`${targetFormType}-${targetFormSource}-${targetFormInstanceKey}`}
                targetType={targetFormType}
                source={targetFormSource}
                onChange={values => setTargetFormResult(JSON.stringify(values, null, 2))}
            />

            {targetFormResult !== null && <Box sx={styles.result}>{targetFormResult}</Box>}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                Target Configurator (2-step)
            </Typography>

            <Box sx={styles.controls}>
                <TextField
                    size="small"
                    label="Target Type"
                    value={targetConfigType}
                    onChange={e => {
                        setTargetConfigType(e.target.value);
                        setTargetConfigInstanceKey(k => k + 1);
                        setTargetConfigResult(null);
                    }}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            <TargetConfiguratorTool
                key={`${targetConfigType}-${targetConfigInstanceKey}`}
                args={{ targetType: targetConfigType }}
                respond={r => setTargetConfigResult(r)}
            />

            {targetConfigResult !== null && (
                <Box sx={styles.result}>
                    {(() => {
                        try {
                            return JSON.stringify(JSON.parse(targetConfigResult), null, 2);
                        } catch {
                            return targetConfigResult;
                        }
                    })()}
                </Box>
            )}
        </Box>
    );
};

export default DebugTools;
